import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { inboundConfigAPI } from '../../services/api';
import type { InboundConfiguration } from './inboundConfigurationTypes';

export function useInboundConfigurationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [inboundCallAssistantId, setInboundCallAssistantId] = useState('');
  const [campaignContext, setCampaignContext] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [existingConfig, setExistingConfig] = useState<InboundConfiguration | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { userAssistantConfigService } = await import('../../services/userAssistantConfigService');
      const userConfig = await userAssistantConfigService.getConfig().catch(() => null);
      if (userConfig?.inbound_assistant_id) {
        setInboundCallAssistantId(userConfig.inbound_assistant_id);
      }
      const configResponse = await inboundConfigAPI.getConfiguration().catch(() => null);
      if (configResponse) {
        setExistingConfig(configResponse);
        setCampaignContext(configResponse.campaign_context || '');
        setIsActive(configResponse.is_active);
        if (configResponse.assistant_id) {
          setInboundCallAssistantId(configResponse.assistant_id);
        }
      }
    } catch (err) {
      setError('Failed to load configuration data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const configData = {
        assistant_id: inboundCallAssistantId,
        campaign_context: campaignContext.trim(),
        is_active: isActive,
      };
      const savedConfig = existingConfig
        ? await inboundConfigAPI.updateConfiguration(configData)
        : await inboundConfigAPI.createOrUpdateConfiguration(configData);
      setExistingConfig(savedConfig);
      setSuccess('Configuration saved and synced with Voice Gateway successfully!');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
    setError(null);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!existingConfig) return;
    try {
      setDeleting(true);
      setError(null);
      await inboundConfigAPI.deleteConfiguration();
      setExistingConfig(null);
      setCampaignContext('');
      setIsActive(true);
      setSuccess('Configuration deleted successfully!');
      closeDeleteModal();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete configuration');
    } finally {
      setDeleting(false);
    }
  };

  return {
    inboundCallAssistantId,
    setInboundCallAssistantId,
    campaignContext,
    setCampaignContext,
    isActive,
    setIsActive,
    loading,
    saving,
    deleting,
    error,
    success,
    existingConfig,
    showDeleteModal,
    loadData,
    handleSave,
    openDeleteModal,
    closeDeleteModal,
    handleDelete,
  };
}
